import { memo, useMemo } from 'react';

import { Copy } from 'lucide-react';
import { isEmpty } from 'remeda';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

import { PromoCodeTableActionColumn } from './promoCodeTableColumns/PromoCodeTableActionColumn';

import { DataTable } from 'v2/components/dataTable/DataTable';
import { LONG_DASH } from 'v2/constants';
import { IPagination, Pageable, TableColumn } from 'v2/types';
import { PromoCodeModel } from 'v2/types/PromoCodeModel';
import { copyText, formatNumber } from 'v2/utils';

interface PromoCodeTableProps {
  data?: Pageable<PromoCodeModel>;
  pagination: IPagination;
  onRevalidate: VoidFunction;
}

export const PromoCodeTable = memo<PromoCodeTableProps>(({ data, pagination, onRevalidate }) => {
  const columns: TableColumn<PromoCodeModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Promo Code Name',
        style: { width: '15%' },
        ellipsis: true,
        render: (row) => (
          <Button endIcon={<Copy width={15} height={15} />} onClick={() => copyText(row.name)}>
            {row.name}
          </Button>
        ),
      },
      {
        key: 'groupName',
        label: 'Group Name',
        render: (row) =>
          isEmpty(row.groupName) ? LONG_DASH : <Chip label={row.groupName} size="small" variant="outlined" />,
      },
      {
        key: 'type',
        label: 'Reward Type',
        render: (row) => <Chip label={row.type} color="info" size="small" />,
      },
      {
        key: 'value',
        label: 'Reward Value',
        ellipsis: true,
        render: (row) => (isNaN(Number(row.value)) ? row.value : formatNumber(row.value)),
      },
      {
        key: 'activationNumber',
        label: 'Number Of Activations',
        render: (row) => formatNumber(row.activationNumber),
      },
      {
        id: 'createdBy',
        key: 'createdBy',
        label: 'Created By',
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <PromoCodeTableActionColumn promoCode={row} onRevalidate={onRevalidate} />,
      },
    ],
    [onRevalidate]
  );

  return (
    <DataTable
      columns={columns}
      data={data?.result}
      total={data?.total}
      page={pagination.page}
      pageSize={pagination.pageSize}
      onPageChange={pagination.onPageChange}
      withPagination
    />
  );
});
